import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { plotDossiersApi } from '../api/plotDossiers';
import { resolveStoryFrameworkMeta, storyFrameworkRef } from '../api/frameworks';
import FrameworkPicker from '../components/Frameworks/FrameworkPicker';
import GenreStandingMark from '../components/Frameworks/GenreStandingMark';
import {
  BOOK_STRUCTURE_HINT,
  BOOK_STRUCTURE_SET,
  BOOK_STRUCTURE_USE,
} from '../components/Frameworks/BookStructureDoor';
import {
  FRAMEWORK_BUILDER_HEADING,
  FRAMEWORK_OPEN_BOOK_LINE,
  FRAMEWORK_SAVE_ERROR,
  THIS_IS_THIS_BOOKS_FRAMEWORK,
} from '../utils/writerCopy';
import { useSyncStoryContext } from '../hooks/useSyncStoryContext';
import StoryPicker from '../components/Common/StoryPicker';
import UploadSettle from '../components/UploadGaps/UploadSettle';
import { uploadPathApi } from '../api/uploadPath';
import './FrameworkBuilderPage.css';

/**
 * Framework Builder room (#917 leftover of #913). Catalog, not a session type.
 * Shows the chosen shape as this book's Framework and a pick-for-this-book verb.
 */
export default function FrameworkBuilderPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const docsSpineSeed = location.state?.docsSpineSeed || null;
  const fromUploadB = location.state?.uploadPath === 'B';
  const openBuilder = location.state?.openBuilder || null;
  const { currentStory, currentProject, setProjectAndStory } = useApp();
  const openedId = currentStory?.id || null;
  const { storyId } = useSyncStoryContext({ routeStoryId: openedId });
  const [pickerOpen, setPickerOpen] = useState(false);
  const [hung, setHung] = useState(null);
  const [error, setError] = useState(null);
  const [pathView, setPathView] = useState(null);
  const projectId = currentProject?.id || currentStory?.project_id || null;

  const loadHung = useCallback(async () => {
    if (!storyId) {
      setHung(null);
      return;
    }
    try {
      const status = await plotDossiersApi.status(storyId);
      const ref = status?.framework_ref || null;
      if (!ref) {
        setHung(null);
        return;
      }
      const meta = await resolveStoryFrameworkMeta(ref);
      setHung({
        ref,
        framework_name: meta?.framework_name || BOOK_STRUCTURE_SET,
        genre_tag: meta?.genre_tag || null,
        thin_genre: Boolean(meta?.thin_genre),
      });
    } catch {
      setHung(null);
    }
  }, [storyId]);

  useEffect(() => { loadHung(); }, [loadHung]);

  useEffect(() => {
    if (!storyId) {
      setPathView(null);
      return undefined;
    }
    let cancelled = false;
    Promise.resolve(uploadPathApi.view(storyId))
      .then((data) => { if (!cancelled) setPathView(data || null); })
      .catch(() => { if (!cancelled) setPathView(null); });
    return () => { cancelled = true; };
  }, [storyId]);

  // Run 41: picking a book here did nothing (the picker had no onPick).
  const handlePick = useCallback((project, story) => {
    setProjectAndStory(project, story);
    setPickerOpen(false);
  }, [setProjectAndStory]);

  const handleSelect = async (dto) => {
    if (!storyId) return false;
    setError(null);
    try {
      await plotDossiersApi.setFramework(storyId, storyFrameworkRef(dto));
      await loadHung();
      if (fromUploadB) navigate('/dossiers?from=upload-b');
      return true;
    } catch (err) {
      setError(err?.message || FRAMEWORK_SAVE_ERROR);
      return false;
    }
  };

  if (!storyId) {
    return (
      <div className="fw-builder-page fw-builder-page--gate">
        <p className="fw-builder-page__kicker">The Book</p>
        <h1>{FRAMEWORK_BUILDER_HEADING}</h1>
        <p>{FRAMEWORK_OPEN_BOOK_LINE}</p>
        <button type="button" className="fw-btn fw-btn--primary" onClick={() => setPickerOpen(true)}>
          Open a book
        </button>
        <StoryPicker
          isOpen={pickerOpen}
          onClose={() => setPickerOpen(false)}
          onPick={handlePick}
          title="Open a book on Framework Builder"
        />
      </div>
    );
  }

  return (
    <div className="fw-builder-page">
      <header className="fw-builder-page__header">
        <UploadSettle
          gaps={pathView?.gaps}
          room="framework"
          onOpenNotes={projectId ? () => navigate(`/projects/${projectId}/stories/${storyId}/notes`) : undefined}
        />
        {hung ? (
          <div className="fw-book-hang" role="status">
            <p className="fw-detail__hang-hint">{THIS_IS_THIS_BOOKS_FRAMEWORK}</p>
            <p className="fw-builder-page__hung-name">{hung.framework_name}</p>
            <GenreStandingMark genre={hung.genre_tag} />
          </div>
        ) : null}
        {error && <p className="fw-error" role="alert">{error}</p>}
      </header>
      <FrameworkPicker
        isOpen
        onClose={() => {}}
        embedded
        mode="select"
        hangKind="book"
        selectLabel={BOOK_STRUCTURE_USE}
        selectHint={BOOK_STRUCTURE_HINT}
        selectedId={hung?.ref || null}
        onSelect={handleSelect}
        storyId={storyId}
        initialEdit={docsSpineSeed}
        openBuilder={openBuilder}
      />
    </div>
  );
}
